<template>
  <!-- 查询表单 -->
  <el-form
    v-if="formList.length"
    ref="formFields"
    :model="searchObj"
    size="small"
    class="fan-search margin-bottom-8"
    :inline="true"
    @submit.native.prevent
  >
    <!-- 查询条件 -->
    <template v-for="(columnItem, keys) in searchList">
      <div :key="`q${keys}`" class="fan-search-col">
        <el-form-item
          :label="columnItem.label ? columnItem.label : ''"
          :prop="columnItem.name"
          :class="{'no-label': Boolean(!columnItem.label)}"
          :rules="columnItem.rules"
        >
          <template v-if="columnItem.slot">
            <slot :name="columnItem.slot" />
          </template>
          <template v-if="columnItem.component === 'input'">
            <el-input
              clearable
              v-model.trim="formParams[columnItem.prop]"
              v-bind="columnItem.attrs"
              @keyup.enter="(val) => handleComponent(val, columnItem, 1)"
            />
          </template>
          <template v-if="columnItem.component === 'search-input-select'">
            <searchInputSelect
              :options="columnItem.options"
              :type.sync="formParams[columnItem.prop]"
              :content.sync="formParams[columnItem.contentProp]"
              v-bind="columnItem.attrs"
            />
          </template>
          <template v-if="columnItem.component === 'select-date-picker'">
            <selectDatePicker
              :options="columnItem.options"
              :date="formParams"
              :type.sync="formParams[columnItem.prop]"
              @setFormFields="setFormFields"
              v-bind="columnItem"
            />
          </template>
          <template v-if="columnItem.component === 'select'">
            <el-select
              clearable
              v-model="formParams[columnItem.prop]"
              :placeholder="columnItem.placeholder"
              :disabled="showDisabled(columnItem)"
              @change="(val) => handleComponent(val, columnItem, 1)"
              :multiple="columnItem.multiple || false"
              :collapse-tags="columnItem.collapseTags || false"
              v-bind="columnItem.attrs"
            >
              <el-option label="全部" value="" v-if="!columnItem.hasAll"></el-option>
              <el-option
                v-for="dictItem in dictDataSource(columnItem)"
                :key="getDictValue(columnItem, dictItem)"
                :label="getDictLabel(columnItem, dictItem)"
                :value="getDictValue(columnItem, dictItem)"
              ></el-option>
            </el-select>
          </template>
        </el-form-item>
      </div>
    </template>

    <div class="fan-search-col fan-search-right">
      <el-button
        type="primary"
        :size="formSize"
        :loading="loading"
        @click="handleSearchClick"
        v-track
        :trackCode="trackCode"
      >
        查询
      </el-button>
      <!-- 兼容批量查询按钮存放 -->
      <slot name="batch-search-btn" />
      <el-button
        :size="formSize"
        :loading="loading"
        @click="handleReset"
        v-if="hasReset"
      >
        重置
      </el-button>
    </div>
  </el-form>
</template>

<script>
import { cloneDeep } from 'lodash';
import selectDatePicker from './components/select-date-picker';
import searchInputSelect from './components/search-input-select';

export default {
  name: 'JrSearch',
  props: {
    // 查询条件列表search-list
    formList: {
      type: Array,
      default: () => {
        return [];
      },
    },
    // 查询参数值
    searchValue: {
      type: Object,
      default: () => {
        return {};
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasReset: {
      type: Boolean,
      default: true,
    },
    defaultParams: {
      default: () => {
        return {};
      },
    },
    trackCode: {
      type: [String, Number],
    },
  },
  components: { selectDatePicker, searchInputSelect },
  data() {
    return {
      searchObj: {},
    };
  },
  computed: {
    searchList() {
      return this.formList;
    },
    formParams: {
      get() {
        return this.searchValue;
      },
      set(val) {
        const objValue = cloneDeep(val);
        this.$emit('update:searchValue', { ...this.searchValue, ...objValue });
      },
    },
    dictDataSource() {
      return (columnItem) => {
        const { dictData } = this.$store.state.dictionary;
        if (columnItem.dictStoreActionName) {
          return dictData[columnItem.dictStoreActionName] || [];
        }
        if (columnItem.dictionaryPath) {
          return dictData[columnItem.dictionaryPath] || [];
        }
        return columnItem?.dictData || [];
      };
    },
    getDictLabel() {
      return (columnItem, dictItem) => (columnItem.dictLabelName ? dictItem[columnItem.dictLabelName] : dictItem?.codeName);
    },
    getDictValue() {
      return (columnItem, dictItem) => (columnItem.dictValueName ? dictItem[columnItem.dictValueName] : dictItem?.value);
    },
    // 判断是否传入disabled
    showDisabled() {
      return (column) => {
        if (typeof column.disabled === 'function') {
        // 传入函数，父级才能获取到this
          return column.disabled();
        } if (typeof column.disabled === 'boolean') {
          return column.disabled;
        }
        return false;
      };
    },
  },
  mounted() {
    const dictPathArr = [];
    const { dictData } = this.$store.state.dictionary;

    this.searchList.forEach((item) => {
      if (item.dictStoreActionName) {
        this.$store.dispatch({
          type: item.dictStoreActionName,
          params: item.dictParams || {},
        });
      }
      if (item.dictionaryPath && !dictData[item.dictionaryPath]) {
        dictPathArr.push(item.dictionaryPath);
      }
    });
    if (dictPathArr.length) {
      this.$store.dispatch({
        type: 'dictionary/getDictionaryPath',
        paths: dictPathArr,
      });
    }
  },
  methods: {
    // 为了自定义组件参数整合
    setFormFields(params = {}) {
      const obj = { ...this.searchObj, ...params };
      this.searchObj = cloneDeep(obj);
      this.$emit('update:searchValue', { ...this.searchValue, ...obj });
    },
    // 查询
    handleSearchClick() {
      const searchObj = cloneDeep(this.searchObj);
      this.$emit('search', { ...this.formParams, ...searchObj });
    },
    // 重置并响应父组件
    handleReset() {
      this.searchObj = {};
      this.$emit('reset', this.defaultParams);
    },
    resetField() {
      this.$refs.formFields.resetFields();
    },
    // 触发事件
    handleComponent(val, columnItem, type) {
      const typeObj = {
        1: 'enter',
        2: 'change',
      };
      if (columnItem.attrs && typeof columnItem.attrs.enter === 'function' && typeObj[type] === 'enter') {
        columnItem.attrs.enter(val);
      }
      if (columnItem.attrs && typeof columnItem.attrs.change === 'function' && typeObj[type] === 'change') {
        columnItem.attrs.change(val);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .el-form-item {
    margin-bottom: 0 !important;
  }
  .el-form-item__label {
    padding-right: 8px;
    color: #2a304d;
    font-size: inherit;
    white-space: nowrap;
  }
  .no-label {
    .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}

.fan-search-col {
  margin-right: 22px;
  &:last-child {
    margin-right: 0;
  }
}
</style>
